/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import type {
  AdvisedProduct,
  SpotlerActivateConfig,
  SpotlerActivateIntegration,
  UnionOfType,
} from "../../../../app/types"
import type { BaseEvent, Product } from "../../types"
import type {
  AdvisorInfo,
  SessionContext,
  UsageAnalyticsEventTransformer, UsageEvent,
} from "../types"

type SpotlerActivateEvent = SpotlerActivateAnsweredEvent | SpotlerActivateAdvisedBaseEvent | SpotlerActivateAdvisedEvent | SpotlerActivateCustomerInfoBaseEvent | SpotlerActivateCustomerInfoEvent | SpotlerActivateInfoPageCustomerInfoEvent | SpotlerActivateNoResultsCustomerInfoEvent | SpotlerActivateEmailOptInEvent | SpotlerActivateAddToCartEvent

type SpotlerActivateBase = UnionOfType<BaseEvent & { integration: "aiden:spotler_activate" }>

type SpotlerActivateAnsweredEvent = UnionOfType<
  SpotlerActivateBase & {
    eventData: {
      event: "AidenAnswer"
      custom_aiden_app_id: string
      custom_aiden_app_name: string
      custom_aiden_question: string
      custom_aiden_answers: string
    }
  }
>

type SpotlerActivateAdvisedBaseEvent = UnionOfType<
  SpotlerActivateBase & {
    eventData: {
      event: string
      custom_aiden_app_id: string
      custom_aiden_app_name: string
      products: { id: string; name: string }[]
    }
  }
>
type SpotlerActivateAdvisedEvent = UnionOfType<
  SpotlerActivateAdvisedBaseEvent & {
  eventData: {
    event: "AidenAdvice"
  }
}
>
type SpotlerActivateCustomerInfoBaseEvent = UnionOfType<
  SpotlerActivateBase & {
  eventData: {
    event: string
    email: string
    custom_aiden_app_id: string
    custom_aiden_checkbox: boolean
    custom_aiden_app_name: string
    products: { id: string; name: string }[]
  }
}
>
type SpotlerActivateCustomerInfoEvent = UnionOfType<
  SpotlerActivateCustomerInfoBaseEvent & {
    eventData: {
      event: "AidenFollowup"
    }
  }
>
type SpotlerActivateInfoPageCustomerInfoEvent = UnionOfType<
  SpotlerActivateBase & {
    eventData: {
      event: "AidenInfoPageFollowup"
      email: string
      custom_aiden_app_id: string
      custom_aiden_checkbox: boolean
      custom_aiden_app_name: string
      custom_aiden_identifier: string
    }
  }
>
type SpotlerActivateNoResultsCustomerInfoEvent = UnionOfType<
  SpotlerActivateBase & {
    eventData: {
      event: "AidenNoResultsFollowup"
      email: string
      custom_aiden_app_id: string
      custom_aiden_checkbox: boolean
      custom_aiden_app_name: string
    }
  }
>
type SpotlerActivateEmailOptInEvent = UnionOfType<
  SpotlerActivateBase & {
    eventData: {
      event: "EmailOptIn"
      email: string
      newsletter: "yes"
      custom_aiden_app_id: string
      custom_aiden_app_name: string
    }
  }
>

type SpotlerActivateAddToCartEvent = UnionOfType<
  SpotlerActivateBase & {
    eventData: {
      event: "AidenAddToCart"
      custom_aiden_app_id: string
      custom_aiden_app_name: string
      products: { id: string; name: string; quantity: number | undefined }[]
    }
  }
>

/**
 *
 * Handles the creation of usage events specific to Spotler Active (formerly Squeezely)
 * If an event-suffix is configured this wrapper copies the Spotler events and adds the suffix to the
 * copied events. This way you can either use the general events in Spotler or the product finder
 * specific events. This is especially useful for product sets because filter options are limited there.
 * */
export const SpotlerActivateUsageEvents = (
  config: SpotlerActivateConfig
): UsageAnalyticsEventTransformer => {
  const baseEvents = SpotlerActivateUsageBaseEvents(config)

  function isSpotlerEvent(
    usageEvent: UsageEvent
  ): usageEvent is SpotlerActivateEvent {
    return "eventData" in usageEvent
  }

  /**
   * If no suffix is configured it returns the original events
   * Otherwise it copies and adds the suffix to all events except for the EmailOptIn event which is a default Spotler event
   */
  function addSuffixEvent(
    events: UsageEvent[]
  ): UsageEvent[] {
    if (!config.eventSuffix) return events
    else {
      const eventsWithSuffix = events
        .filter((e) => isSpotlerEvent(e) && e.eventData.event.toLowerCase() !== "emailoptin")
        .map((e) => {
          if (isSpotlerEvent(e))
            return {
              ...e,
              eventData: {
                ...e.eventData,
                event: e.eventData.event + "_" + config.eventSuffix,
              },
            }
          else {
            return e
          }
        })
      return [...events, ...eventsWithSuffix]
    }
  }

  return {
    questionAnswered: (
      advisor,
      context,
      integrations,
      question,
      answers
    ): UsageEvent[] => {
      return addSuffixEvent(baseEvents.questionAnswered(advisor, context, integrations, question, answers))
    },

    adviceGiven: (
      advisor,
      context,
      integrations,
      products
    ): UsageEvent[] => {
      return addSuffixEvent(baseEvents.adviceGiven(advisor, context, integrations, products))
    },

    customerInfoSubmitted: (
      advisor,
      context,
      integrations,
      isPreview,
      info: {
        email: string
        checkboxChecked: boolean
        checkboxText: string
        advisedProducts: AdvisedProduct[]
      }
    ): UsageEvent[] => {
      return addSuffixEvent(baseEvents.customerInfoSubmitted(advisor, context, integrations, isPreview, info))
    },

    infoPageCustomerInfoSubmitted: (
      advisor: AdvisorInfo,
      context,
      integrations,
      isPreview,
      info: {
        email: string
        checkboxChecked: boolean
        checkboxText: string
        formIdentifier: string
      }
    ): UsageEvent[] => {
      return addSuffixEvent(baseEvents.infoPageCustomerInfoSubmitted(advisor, context, integrations, isPreview, info))
    },

    noResultsCustomerInfoSubmitted: (
      advisor: AdvisorInfo,
      context,
      integrations,
      isPreview,
      info: {
        email: string
        checkboxChecked: boolean
        checkboxText: string
      }
    ): UsageEvent[] => {
      return addSuffixEvent(baseEvents.noResultsCustomerInfoSubmitted(advisor, context, integrations, isPreview, info))
    },

    //
    // These events don't have a usecase (yet)
    //
    clickedOnProduct: () => [],
    conversationStarted: () => [],
  }
}

export const SpotlerActivateUsageBaseEvents = (
  config: SpotlerActivateConfig
): UsageAnalyticsEventTransformer => {
  return {
    questionAnswered: (
      advisor,
      context,
      integrations,
      question,
      answers
    ): SpotlerActivateAnsweredEvent[] => {
      return [
        {
          advisorId: advisor.id,
          advisorName: advisor.name,
          integration: "aiden:spotler_activate",
          eventData: {
            event: "AidenAnswer",
            custom_aiden_app_id: advisor.id,
            custom_aiden_app_name: advisor.name,
            custom_aiden_question: question.title,
            custom_aiden_answers: answers.join(", "),
          },
        },
      ]
    },

    adviceGiven: (
      advisor,
      context,
      integrations,
      products
    ): (SpotlerActivateAdvisedEvent)[] => {
      const event: SpotlerActivateAdvisedEvent = {
        advisorId: advisor.id,
        advisorName: advisor.name,
        integration: "aiden:spotler_activate",
        eventData: {
          event: "AidenAdvice",
          custom_aiden_app_id: advisor.id,
          custom_aiden_app_name: advisor.name,
          products: formatProducts(config, products),
        },
      }

      return [
        event
      ]
    },

    customerInfoSubmitted: (
      advisor,
      context,
      integrations,
      isPreview,
      info: {
        email: string
        checkboxChecked: boolean
        advisedProducts: AdvisedProduct[]
      }
    ): (SpotlerActivateCustomerInfoEvent | SpotlerActivateEmailOptInEvent)[] => {
      if (isPreview) return []
      if (!info.advisedProducts) return []
      const base = makeBaseEvent(advisor)
      const eventData = makeSpotlerActivateEventData<"AidenFollowup">(
        "AidenFollowup",
        advisor,
        info.email,
        info.checkboxChecked
      )

      const infoEvent: SpotlerActivateCustomerInfoEvent = {
        ...base,
        eventData: {
          ...eventData,
          products: formatProducts(
            config,
            info.advisedProducts.map((p) => {
              return {
                clientProductId: p.id,
                data: p.data,
                id: p.id,
                name: p.name,
                image: p.imageUrl,
                link: p.url,
              }
            })
          ),
        },
      }

      if (info.checkboxChecked) {
        return [infoEvent, makeOptInEvent(advisor, info.email)]
      }

      return [infoEvent]
    },

    infoPageCustomerInfoSubmitted: (
      advisor: AdvisorInfo,
      context,
      integrations,
      isPreview,
      info: {
        email: string
        checkboxChecked: boolean
        formIdentifier: string
      }
    ): (SpotlerActivateInfoPageCustomerInfoEvent | SpotlerActivateEmailOptInEvent)[] => {
      if (isPreview) return []
      const base = makeBaseEvent(advisor)
      const eventData = makeSpotlerActivateEventData<"AidenInfoPageFollowup">(
        "AidenInfoPageFollowup",
        advisor,
        info.email,
        info.checkboxChecked
      )
      const infoEvent: SpotlerActivateInfoPageCustomerInfoEvent = {
        ...base,
        eventData: {
          ...eventData,
          custom_aiden_identifier: info.formIdentifier,
        },
      }
      if (info.checkboxChecked)
        return [infoEvent, makeOptInEvent(advisor, info.email)]

      return [infoEvent]
    },

    noResultsCustomerInfoSubmitted: (
      advisor: AdvisorInfo,
      context,
      integrations,
      isPreview,
      info: {
        email: string
        checkboxChecked: boolean
      }
    ): (SpotlerActivateNoResultsCustomerInfoEvent | SpotlerActivateEmailOptInEvent)[] => {
      if (isPreview) return []
      const base = makeBaseEvent(advisor)
      const eventData = makeSpotlerActivateEventData<"AidenNoResultsFollowup">(
        "AidenNoResultsFollowup",
        advisor,
        info.email,
        info.checkboxChecked
      )
      const infoEvent: SpotlerActivateNoResultsCustomerInfoEvent = {
        ...base,
        eventData: eventData,
      }

      if (info.checkboxChecked)
        return [infoEvent, makeOptInEvent(advisor, info.email)]

      return [infoEvent]
    },

    //
    // These events don't have a usecase (yet)
    //
    clickedOnProduct: () => [],
    conversationStarted: () => [],
  }
}

function makeSpotlerActivateEventData<T>(
  eventName: T,
  advisor: AdvisorInfo,
  email: string,
  checkboxChecked: boolean
) {
  return {
    event: eventName,
    email: email,
    custom_aiden_app_id: advisor.id,
    custom_aiden_app_name: advisor.name,
    custom_aiden_checkbox: checkboxChecked,
  }
}

function makeOptInEvent(
  advisor: AdvisorInfo,
  email: string
): SpotlerActivateEmailOptInEvent {
  return {
    ...makeBaseEvent(advisor),
    eventData: {
      event: "EmailOptIn",
      email: email,
      newsletter: "yes",
      custom_aiden_app_id: advisor.id,
      custom_aiden_app_name: advisor.name,
    },
  }
}

function makeBaseEvent(advisor: AdvisorInfo): {
  advisorId: string
  advisorName: string
  integration: "aiden:spotler_activate"
} {
  return {
    advisorId: advisor.id,
    advisorName: advisor.name,
    integration: "aiden:spotler_activate",
  }
}

/**
 * Which product data to use as the ID sent to Spotler Activate can be configured.
 * This function replaces the clientProductId with the value of the configured field.
 */
function withConfiguredProductId(
  config: SpotlerActivateConfig,
  product: Product
): Product {
  if (typeof config.idField === "string") {
    return {
      ...product,
      clientProductId:
        replacementToString(product.data[config.idField]) ||
        product.clientProductId,
    }
  }
  return product
}

function replacementToString(replacement: unknown): string | null {
  if (Array.isArray(replacement)) {
    const first = replacement[0]
    if (!first) return null
    return replacementToString(first)
  }
  if (typeof replacement === "string") {
    return replacement
  }
  if (typeof replacement === "number") {
    return `${replacement}`
  }
  return null
}

function formatProducts(
  config: SpotlerActivateConfig,
  products: Product[]
): { id: string; name: string }[] {
  return products
    .map((product) => withConfiguredProductId(config, product))
    .map((product) => ({
      id: product.clientProductId,
      name: product.name,
    }))
}

function formatQuantityProducts(
  config: SpotlerActivateConfig,
  products: Product[]
): { id: string; name: string; quantity: number | undefined }[] {
  return products
    .map((product) => withConfiguredProductId(config, product))
    .map((product) => ({
      id: product.clientProductId,
      name: product.name,
      quantity: product.quantity,
    }))
}
